/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import { StructuredText, renderRule } from 'react-datocms';
import {
    isHeading,
    isParagraph,
    isList,
    isLink,
    isListItem
} from 'datocms-structured-text-utils';
import { object, bool, oneOf, string } from 'prop-types';
import commonOptionsPropType from 'TMPropTypes/commonOptionsProps';
import styled, { useTheme } from 'styled-components';
import List from 'TMComponents/List';
import ListItem from 'TMComponents/List/ListItem';
import NavLink from 'TMComponents/NavLink';
import Image from 'TMComponents/Image';
import { Button } from 'TMComponents/Button';
import SharedOptions from 'TMComponents/ComponentMapper/SharedOptions';
import Container from 'TMComponents/Container';
import {
    H1,
    H2,
    H3,
    H4,
    H5,
    H6,
    BodyReg,
    BodySm,
    BodyXs
} from 'TMComponents/Typography';
import { Link as LinkStyle } from 'TMComponents/Typography/styles';
import { breakpoints } from 'TMConstants';
import Arrow from 'TMComponents/Icons/Arrow';

const Wrapper = styled(Container)`
    position: relative;
    max-width: 768px;
    width: 100%;
    position: 'relative';

    ${({ whiteText, theme }) =>
        whiteText && `color: ${theme.colorSystem.UIText[3]} !important;`}
    ${({ smallMargins }) =>
        smallMargins &&
        `
        h1,h2,h3,h4,h5,h6,p {
            margin-bottom: 0;
        }
    `}

    @media (max-width: ${breakpoints.tabletDesktop}px) {
        ${({ mobileCenterAlignText }) =>
            mobileCenterAlignText && `text-align: center;`}
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        ${({ centerAlignText }) =>
            centerAlignText && `text-align: center;`}
    }
`;

const Link = styled(NavLink)`
    ${LinkStyle};

    ${({ whiteText, theme }) =>
        whiteText && `color: ${theme.colorSystem.UIText[3]} !important;`};

    ${({ linkWithIcon }) =>
        linkWithIcon &&
        `
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        gap: 0.5rem;    
        `}

    ${({ noUnderline }) => noUnderline && `text-decoration: none;`}
`;

const ContentParserStructured = ({
    structuredCopy,
    listCheckMarks,
    listTickVariant,
    bodyCopyVariant = 'default',
    smallMargins,
    allowH1,
    leftAlign,
    centerAlignText,
    mobileCenterAlignText,
    textColor,
    linkWithIcon,
    noUnderline,
    commonOptions
}) => {
    const theme = useTheme();

    const whiteText = textColor === 'light';

    return (
        <Wrapper
            leftAlign={leftAlign}
            centerAlignText={centerAlignText}
            mobileCenterAlignText={mobileCenterAlignText}
            smallMargins={smallMargins}
            whiteText={whiteText}>
            <StructuredText
                data={structuredCopy}
                renderBlock={({ record }) => {
                    const {
                        __typename,
                        hideOnMobile,
                        hideOnDesktop,
                        whiteBackground,
                        ...item
                    } = record;
                    const ComponentDictionary = {
                        DatoCmsImage: Image,
                        DatoCmsButton: Button
                    };
                    const Component = ComponentDictionary[__typename];
                    if (!Component) return null;
                    return (
                        <SharedOptions
                            whiteBackground={whiteBackground}
                            hideOnMobile={hideOnMobile}
                            hideOnDesktop={hideOnDesktop}
                            noSideMargins={
                                whiteBackground || item.imageCover
                            }>
                            <Component
                                {...item}
                                leftAlign={!centerAlignText}
                            />
                        </SharedOptions>
                    );
                }}
                customNodeRules={[
                    renderRule(isHeading, ({ node, children, key }) => {
                        const variant = commonOptions?.titleVariant;
                        switch (node.level) {
                            case 1:
                                if (!allowH1)
                                    return (
                                        <H2 key={key} variant={variant}>
                                            {children}
                                        </H2>
                                    );
                                return (
                                    <H1 key={key} variant={variant}>
                                        {children}
                                    </H1>
                                );

                            case 3:
                                return (
                                    <H3 key={key} variant={variant}>
                                        {children}
                                    </H3>
                                );
                            case 4:
                                return (
                                    <H4 key={key} variant={variant}>
                                        {children}
                                    </H4>
                                );
                            case 5:
                                return (
                                    <H5 key={key} variant={variant}>
                                        {children}
                                    </H5>
                                );
                            case 6:
                                return (
                                    <H6 key={key} variant={variant}>
                                        {children}
                                    </H6>
                                );
                            default:
                                return (
                                    <H2 key={key} variant={variant}>
                                        {children}
                                    </H2>
                                );
                        }
                    }),

                    renderRule(isParagraph, ({ children, key }) => {
                        switch (bodyCopyVariant) {
                            case 'small':
                                return (
                                    <BodySm key={key}>{children}</BodySm>
                                );
                            case 'extra_small':
                                return (
                                    <BodyXs key={key}>{children}</BodyXs>
                                );
                            default:
                                return (
                                    <BodyReg key={key}>{children}</BodyReg>
                                );
                        }
                    }),

                    renderRule(isList, ({ node, children, key }) => (
                        <List
                            key={key}
                            markerStyle={node.style}
                            listCheckMarks={listCheckMarks}
                            listTickVariant={listTickVariant}
                            centerAlignText={centerAlignText}
                            mobileCenterAlignText={mobileCenterAlignText}>
                            {children}
                        </List>
                    )),

                    renderRule(isListItem, ({ key, children }) => (
                        <ListItem
                            key={key}
                            listCheckMarks={listCheckMarks}
                            listTickVariant={listTickVariant}>
                            {children}
                        </ListItem>
                    )),
                    renderRule(isLink, ({ node, children, key }) => (
                        <Link
                            key={key}
                            href={node.url}
                            whiteText={whiteText}
                            linkWithIcon={linkWithIcon}
                            noUnderline={noUnderline}>
                            {children}
                            {linkWithIcon && (
                                <Arrow
                                    color={
                                        textColor === 'light'
                                            ? theme.colorSystem.UIText[3]
                                            : theme.colorSystem.primary[4]
                                    }
                                />
                            )}
                        </Link>
                    ))
                ]}
            />
        </Wrapper>
    );
};

ContentParserStructured.propTypes = {
    structuredCopy: object.isRequired,
    listCheckMarks: bool,
    listTickVariant: oneOf(['blue', 'green']),
    leftAlign: bool,
    smallMargins: bool,
    bodyCopyVariant: oneOf(['default', 'small', 'extra_small']),
    mobileCenterAlignText: bool,
    textColor: oneOf(['dark', 'light']),
    allowH1: bool,
    centerAlignText: bool,
    backgroundVariant: string,
    linkWithIcon: bool,
    noUnderline: bool,
    ...commonOptionsPropType
};

export default ContentParserStructured;
